// src/features/riskmize/IncidentsDrawer.jsx
//
// Docked panel that lives opposite the vehicles/assets panel. Renders either
// a filtered+sorted list of incidents (drawerMode === 'list') or the
// IncidentDetailPanel (drawerMode === 'detail').
//
// @param {'start'|'end'} assetsPanelSide - logical side of the assets panel
//   (from useResizablePanel). The drawer is rendered on the opposite side.
// @param {(coords: [number, number]) => void} onZoomTo - invoked with
//   [lng, lat] when a row is clicked; map host typically flyTo()s the map.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, ShieldAlert, MapPin, ChevronDown, Check, Clock } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import { riskmizeActions } from '@/store';
import IncidentDetailPanel from './IncidentDetailPanel';
import { SEVERITY_COLORS } from './severity';

dayjs.extend(relativeTimePlugin);

const DRAWER_WIDTH = 320;

const TIME_RANGES = [
  { value: '24h', labelKey: 'riskmize.range24h', fallback: '24h' },
  { value: '48h', labelKey: 'riskmize.range48h', fallback: '48h' },
  { value: '7d',  labelKey: 'riskmize.range7d',  fallback: '7d'  },
  { value: '30d', labelKey: 'riskmize.range30d', fallback: '30d' },
];

const SEVERITIES = ['critical', 'high', 'medium', 'low'];

/**
 * Pull a Point feature's coords in [lng, lat] order. Handles both full GeoJSON
 * features and trimmed row shapes that carry latitude/longitude at top level.
 */
function getCoords(incident) {
  const geom = incident?.geometry;
  if (geom?.type === 'Point' && Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
    return [geom.coordinates[0], geom.coordinates[1]];
  }
  if (incident?.properties?.longitude != null && incident?.properties?.latitude != null) {
    return [Number(incident.properties.longitude), Number(incident.properties.latitude)];
  }
  if (incident?.longitude != null && incident?.latitude != null) {
    return [Number(incident.longitude), Number(incident.latitude)];
  }
  return null;
}

/** Read a property — incidents can arrive as bare objects or as GeoJSON features. */
function p(incident, key) {
  return incident?.[key] ?? incident?.properties?.[key];
}

export default function IncidentsDrawer({ assetsPanelSide, onZoomTo }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const drawerOpen = useSelector((s) => s.riskmize.drawerOpen);
  const drawerMode = useSelector((s) => s.riskmize.drawerMode);
  const incidents = useSelector((s) => s.riskmize.incidents);
  const filters = useSelector((s) => s.riskmize.filters);

  // Opposite side of the assets panel (so vehicles left → incidents right, etc.)
  const drawerSide = assetsPanelSide === 'start' ? 'end' : 'start';

  // Unique {id, name} pairs across the currently-loaded set — feeds the dropdown.
  // Derived BEFORE the category filter so selecting categories doesn't shrink the menu.
  const allCategories = useMemo(() => {
    const seen = new Map();
    for (const inc of incidents || []) {
      const id = p(inc, 'category_id');
      const name = p(inc, 'category_name');
      if (id && name && !seen.has(id)) seen.set(id, name);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [incidents]);

  const sorted = useMemo(() => {
    const list = [...(incidents || [])];
    const catFilter = filters.category ?? [];
    const filtered = catFilter.length === 0
      ? list
      : list.filter((inc) => catFilter.includes(p(inc, 'category_id')));
    filtered.sort((a, b) => {
      const ta = new Date(p(a, 'incident_date') || p(a, 'created_at') || 0).getTime();
      const tb = new Date(p(b, 'incident_date') || p(b, 'created_at') || 0).getTime();
      return tb - ta;
    });
    return filtered;
  }, [incidents, filters.category]);

  // Tick state to keep "28 minutes ago" fresh without a full rerender chain.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Category dropdown open/close with outside-click handling.
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  const catMenuRef = useRef(null);
  useEffect(() => {
    if (!catMenuOpen) return undefined;
    const onDoc = (e) => {
      if (catMenuRef.current && !catMenuRef.current.contains(e.target)) setCatMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [catMenuOpen]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') dispatch(riskmizeActions.closeDrawer());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, dispatch]);

  if (!drawerOpen) return null;

  const toggleSeverity = (sev) => {
    const current = filters.severity ?? [];
    const next = current.includes(sev) ? current.filter((s) => s !== sev) : [...current, sev];
    dispatch(riskmizeActions.setFilters({ severity: next }));
  };

  const toggleCategory = (id) => {
    const current = filters.category ?? [];
    const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
    dispatch(riskmizeActions.setFilters({ category: next }));
  };

  const clearCategories = () => dispatch(riskmizeActions.setFilters({ category: [] }));

  const setTimeRange = (range) => {
    dispatch(riskmizeActions.setFilters({ timeRange: range }));
  };

  return (
    <div
      className="absolute top-12 bottom-0 z-20 flex flex-col shadow-2xl border-s border-slate-200/80 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/95 transition-[inset] duration-200"
      style={{
        width: DRAWER_WIDTH,
        [drawerSide === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: 12,
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200/80 dark:border-slate-700/60">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-rose-600" />
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('riskmize.drawerTitle', 'Incidents')}
          </h2>
          {drawerMode === 'list' && (
            <span className="text-[10px] font-medium text-slate-400 tabular-nums">
              {sorted.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => dispatch(riskmizeActions.closeDrawer())}
          className="p-1 rounded-md text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label={t('common.close', 'Close')}
        >
          <X size={14} />
        </button>
      </div>

      {drawerMode === 'detail' ? (
        <IncidentDetailPanel />
      ) : (
        <>
          {/* Filters */}
          <div className="px-3 py-2 border-b border-slate-200/80 dark:border-slate-700/60 space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              {TIME_RANGES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setTimeRange(r.value)}
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-md transition-colors ${
                    filters.timeRange === r.value
                      ? 'bg-rose-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  {t(r.labelKey, r.fallback)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {SEVERITIES.map((sev) => {
                const active = (filters.severity ?? []).includes(sev);
                return (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => toggleSeverity(sev)}
                    className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md transition-all"
                    style={{
                      background: active ? SEVERITY_COLORS[sev] : 'transparent',
                      color: active ? '#ffffff' : SEVERITY_COLORS[sev],
                      border: `1px solid ${SEVERITY_COLORS[sev]}`,
                    }}
                  >
                    {sev}
                  </button>
                );
              })}
            </div>
            {allCategories.length > 0 && (
              <div className="relative" ref={catMenuRef}>
                <button
                  type="button"
                  onClick={() => setCatMenuOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-1.5 text-[10px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 transition-colors"
                >
                  <span className="truncate">
                    {(filters.category ?? []).length === 0
                      ? t('riskmize.allTypes', 'All types')
                      : t('riskmize.typesSelected', '{{n}} type(s) selected', { n: filters.category.length })}
                  </span>
                  <ChevronDown size={12} className="flex-shrink-0 opacity-70" />
                </button>
                {catMenuOpen && (
                  <div className="absolute z-30 mt-1 start-0 end-0 max-h-60 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1">
                    {(filters.category ?? []).length > 0 && (
                      <button
                        type="button"
                        onClick={clearCategories}
                        className="w-full text-start text-[10px] font-medium px-2.5 py-1.5 text-rose-600 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        {t('common.clear', 'Clear')}
                      </button>
                    )}
                    {allCategories.map((cat) => {
                      const active = (filters.category ?? []).includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => toggleCategory(cat.id)}
                          className="w-full flex items-center gap-2 text-start text-[11px] px-2.5 py-1.5 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          <span className="w-3.5 h-3.5 rounded border border-slate-300 dark:border-slate-600 flex items-center justify-center flex-shrink-0">
                            {active && <Check size={10} className="text-rose-600" />}
                          </span>
                          <span className="truncate">{cat.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {sorted.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400 dark:text-slate-500">
                {t('riskmize.emptyList', 'No incidents in range')}
              </div>
            ) : sorted.map((inc) => {
              const id = p(inc, 'id');
              const severity = p(inc, 'severity') || 'low';
              const title = p(inc, 'title') || t('riskmize.untitledIncident', 'Untitled incident');
              const country = p(inc, 'country');
              const province = p(inc, 'province');
              const category = p(inc, 'category_name');
              const img = p(inc, 'image_url');
              const coords = getCoords(inc);
              const when = p(inc, 'incident_date') || p(inc, 'created_at');
              const rel = when ? dayjs(when).fromNow() : null;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    dispatch(riskmizeActions.selectIncident(id));
                    if (coords && onZoomTo) onZoomTo(coords);
                  }}
                  className="w-full text-start flex items-start gap-2.5 px-3 py-2 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {/* Thumb */}
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
                       style={{ background: SEVERITY_COLORS[severity] || SEVERITY_COLORS.low }}>
                    {img ? (
                      <img src={img} alt="" className="w-full h-full object-cover"
                           onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <ShieldAlert size={16} className="text-white/80" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {title}
                    </div>
                    {(province || country) && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                        <MapPin size={9} />
                        <span className="truncate">
                          {[province, country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {rel && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                        <Clock size={9} />
                        <span className="truncate" title={when}>{rel}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider text-white px-1.5 py-0.5 rounded"
                        style={{ background: SEVERITY_COLORS[severity] }}
                      >
                        {severity}
                      </span>
                      {category && (
                        <span className="text-[9px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                          {category}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
